import React, {
  cloneElement,
  JSXElementConstructor,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { callAll } from '@utils'
import { MorphBox } from './BusinessMenu.styled'
import { CompactList } from './CompactList'

const COMPACT_MAX_WIDTH = 760
const EXPANDED_MAX_WIDTH = 1320
const TRIGGER_GAP = 10

interface BusinessMenuProps {
  children: ReactElement<any, string | JSXElementConstructor<any>>
}

/**
 * The sidebar business switcher: one dropdown anchored under the trigger
 * (search + the columns of the user's saved table configuration). Expanding
 * just widens the same panel to fit every saved column — no overlay, no
 * second card. Geometry is animated by the MorphBox transitions.
 */
export const BusinessMenu: React.FC<BusinessMenuProps> = ({ children }) => {
  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))
  const triggerRef = useRef<HTMLElement | null>(null)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const [isOpen, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [search, setSearch] = useState('')
  const [anchor, setAnchor] = useState({ left: 12, top: 58 })
  const [contentHeight, setContentHeight] = useState(0)
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  const location = useLocation()

  const close = useCallback(() => {
    setOpen(false)
    setExpanded(false)
    setSearch('')
  }, [])

  // Any navigation (row click, back button, …) dismisses the menu; the
  // panel itself never needs to know where the app went.
  const isFirstLocation = useRef(true)
  useEffect(() => {
    if (isFirstLocation.current) {
      isFirstLocation.current = false
      return
    }
    close()
  }, [location.pathname, close])

  const measureAnchor = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) setAnchor({ left: rect.left, top: rect.bottom + TRIGGER_GAP })
  }, [])

  // Layout effect: the anchor, viewport and initial content height must be
  // known before the first paint, otherwise the panel paints at the default
  // geometry and visibly slides into place on the first open after load.
  // (The viewport is re-read here because the resize listener below only
  // runs while the menu is open — the window may have changed in between.)
  useLayoutEffect(() => {
    if (!isOpen) return
    measureAnchor()
    setViewport({ width: window.innerWidth, height: window.innerHeight })
    const height = contentRef.current?.offsetHeight
    if (height) setContentHeight(height)
  }, [isOpen, measureAnchor])

  useEffect(() => {
    if (!isOpen) return
    // rAF-throttled: resize fires continuously while dragging, but one
    // geometry update per frame is all the morph box can use.
    let frame = 0
    const onResize = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measureAnchor()
        setViewport({ width: window.innerWidth, height: window.innerHeight })
      })
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [isOpen, measureAnchor])

  // The box animates between concrete heights, so the content height must
  // be measured in both states.
  useEffect(() => {
    if (!isOpen) return
    const el = contentRef.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      const height = el.offsetHeight
      if (height) setContentHeight(height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (expanded) setExpanded(false)
      else close()
    }
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (boxRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [isOpen, expanded, close])

  // Expanding widens the anchored dropdown rightward to fit every saved
  // column; nothing else about the geometry changes. The list fits its
  // page size to the same available height (see CompactList).
  const availableHeight = viewport.height - anchor.top - TRIGGER_GAP
  const maxWidth = expanded ? EXPANDED_MAX_WIDTH : COMPACT_MAX_WIDTH
  const width = Math.min(maxWidth, viewport.width - anchor.left - 24)
  const height = Math.min(contentHeight || 320, availableHeight)

  const geometry = {
    left: Math.round(anchor.left),
    top: Math.round(anchor.top),
    width: Math.round(width),
    height: Math.round(height)
  }

  // Geometry is written straight to the DOM node instead of through the
  // motion style prop: framer re-applies its style on every animation
  // frame, which keeps restarting the CSS geometry transition and leaves
  // it stuck partway. Imperative writes let the CSS morph run untouched
  // while framer only owns the fade/scale.
  useLayoutEffect(() => {
    const node = boxRef.current
    if (!isOpen || !node) return
    node.style.left = `${geometry.left}px`
    node.style.top = `${geometry.top}px`
    node.style.width = `${geometry.width}px`
    node.style.height = `${geometry.height}px`
  }, [isOpen, geometry.left, geometry.top, geometry.width, geometry.height])

  return (
    <>
      {cloneElement(children, {
        ref: (node: HTMLElement | null) => {
          triggerRef.current = node
        },
        'aria-expanded': isOpen,
        onClick: callAll(() => {
          if (isOpen) close()
          else setOpen(true)
        }, children.props.onClick)
      })}
      {rootRef.current &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <MorphBox ref={boxRef}>
                {/* Scroll container so the list stays reachable when the
                    measured height gets capped by the viewport; the inner
                    div is never height-constrained, so measuring it always
                    yields the natural content height. */}
                <div style={{ height: '100%', overflowY: 'auto' }}>
                  <div ref={contentRef}>
                    <CompactList
                      search={search}
                      onSearchChange={setSearch}
                      expanded={expanded}
                      onToggleExpanded={() => setExpanded(current => !current)}
                      onRequestClose={close}
                      maxHeight={availableHeight}
                    />
                  </div>
                </div>
              </MorphBox>
            )}
          </AnimatePresence>,
          rootRef.current
        )}
    </>
  )
}
